import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getIntlLocale } from "@/i18n";
import { Badge, Button, TruncatedText } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  addDaysToDateKey,
  buildWeekDateKeys,
  parseCalendarDateKey,
  todayCalendarDateKey,
  weekStartFromDateKey,
  type CalendarDateKey,
} from "./bookingCalendar";
import type { CalendarDayTotals } from "./bookingCalendar";

interface BookingsCalendarProps {
  selectedDateKey: CalendarDateKey;
  onSelectDate: (dateKey: CalendarDateKey) => void;
  /** Booking and guest totals per activity-local date (missing = none). */
  dayTotals: ReadonlyMap<CalendarDateKey, CalendarDayTotals>;
}

function formatWeekMonthTitle(weekStart: CalendarDateKey): string {
  const weekEnd = addDaysToDateKey(weekStart, 6);
  const start = parseCalendarDateKey(weekStart);
  const end = parseCalendarDateKey(weekEnd);
  const locale = getIntlLocale();

  if (start.year === end.year && start.month === end.month) {
    return new Date(Date.UTC(start.year, start.month, 1)).toLocaleDateString(
      locale,
      { month: "long", year: "numeric", timeZone: "UTC" },
    );
  }

  const startLabel = new Date(
    Date.UTC(start.year, start.month, 1),
  ).toLocaleDateString(locale, {
    month: "short",
    year: start.year === end.year ? undefined : "numeric",
    timeZone: "UTC",
  });
  const endLabel = new Date(Date.UTC(end.year, end.month, 1)).toLocaleDateString(
    locale,
    { month: "short", year: "numeric", timeZone: "UTC" },
  );
  return `${startLabel} – ${endLabel}`;
}

function weekdayShort(dateKey: CalendarDateKey): string {
  const { year, month, day } = parseCalendarDateKey(dateKey);
  return new Date(Date.UTC(year, month, day)).toLocaleDateString(
    getIntlLocale(),
    { weekday: "short", timeZone: "UTC" },
  );
}

export function BookingsCalendar({
  selectedDateKey,
  onSelectDate,
  dayTotals,
}: BookingsCalendarProps) {
  const { t } = useTranslation("bookings");
  const todayKey = todayCalendarDateKey();
  const weekStart = weekStartFromDateKey(selectedDateKey);
  const weekKeys = useMemo(() => buildWeekDateKeys(weekStart), [weekStart]);
  const selectedChipRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const chip = selectedChipRef.current;
    if (!chip || chip.offsetParent === null) return;
    chip.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedDateKey]);

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            aria-label={t("calendar.previousWeek")}
            onClick={() => onSelectDate(addDaysToDateKey(selectedDateKey, -7))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <TruncatedText
            as="h3"
            className="flex-1 text-center text-sm font-semibold capitalize tracking-tight text-surface-900 sm:text-[15px]"
          >
            {formatWeekMonthTitle(weekStart)}
          </TruncatedText>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            aria-label={t("calendar.nextWeek")}
            onClick={() => onSelectDate(addDaysToDateKey(selectedDateKey, 7))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="shrink-0 text-primary-700 dark:text-primary-800"
          onClick={() => onSelectDate(todayKey)}
        >
          {t("calendar.today")}
        </Button>
      </div>

      <div className="-mx-1 flex snap-x snap-mandatory gap-2 overflow-x-auto px-1 pb-1 sm:hidden">
        {weekKeys.map((dateKey) => {
          const bookingCount = dayTotals.get(dateKey)?.bookings ?? 0;
          const isSelected = selectedDateKey === dateKey;
          const isToday = dateKey === todayKey;

          return (
            <button
              key={dateKey}
              ref={isSelected ? selectedChipRef : undefined}
              type="button"
              onClick={() => onSelectDate(dateKey)}
              className={cn(
                "flex w-[4.25rem] shrink-0 snap-center flex-col items-center gap-1 rounded-xl border px-1.5 py-2 transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40",
                isSelected
                  ? "border-primary-500 bg-surface-0 shadow-xs ring-1 ring-primary-500"
                  : "border-surface-200 bg-surface-0",
                isToday && !isSelected && "border-primary-200",
              )}
              aria-label={t("calendar.openDay", {
                date: dateKey,
                count: bookingCount,
              })}
              aria-pressed={isSelected}
            >
              {isToday ? (
                <span className="text-[10px] font-semibold leading-3 text-primary-600">
                  {t("calendar.todayBadge")}
                </span>
              ) : (
                <span className="h-3" aria-hidden />
              )}
              <span className="text-[11px] font-medium capitalize text-surface-500">
                {weekdayShort(dateKey)}
              </span>
              <span
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full text-[15px] font-semibold tabular-nums",
                  isSelected || isToday
                    ? "bg-primary-fill text-white"
                    : "text-surface-900",
                )}
              >
                {parseCalendarDateKey(dateKey).day}
              </span>
              <span
                className={cn(
                  "h-1 w-1 rounded-full",
                  bookingCount > 0 ? "bg-primary-500" : "bg-transparent",
                )}
                aria-hidden
              />
            </button>
          );
        })}
      </div>

      <div className="hidden grid-cols-7 gap-2 sm:grid">
        {weekKeys.map((dateKey) => {
          const bookingCount = dayTotals.get(dateKey)?.bookings ?? 0;
          const covers = dayTotals.get(dateKey)?.guests ?? 0;
          const isSelected = selectedDateKey === dateKey;
          const isToday = dateKey === todayKey;
          const hasBookings = bookingCount > 0;

          return (
            <button
              key={dateKey}
              type="button"
              onClick={() => onSelectDate(dateKey)}
              className={cn(
                "flex min-h-[5.75rem] flex-col gap-2 rounded-lg border p-2.5 text-left transition-[border-color,background-color,box-shadow]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40",
                isSelected
                  ? "border-primary-500 bg-primary-50/60 ring-1 ring-primary-500 dark:bg-primary-50/40"
                  : "border-surface-200 bg-surface-0 hover:border-surface-300 hover:bg-surface-50 dark:hover:bg-surface-100/50",
              )}
              aria-label={t("calendar.openDay", {
                date: dateKey,
                count: bookingCount,
              })}
              aria-pressed={isSelected}
            >
              <div className="flex items-start justify-between gap-1">
                <div className="min-w-0">
                  <TruncatedText
                    as="p"
                    className="text-xs font-medium capitalize text-surface-500"
                  >
                    {weekdayShort(dateKey)}
                  </TruncatedText>
                  <p
                    className={cn(
                      "mt-1 text-xl font-semibold leading-none tracking-tight tabular-nums",
                      isToday ? "text-primary-700" : "text-surface-900",
                    )}
                  >
                    {parseCalendarDateKey(dateKey).day}
                  </p>
                </div>
                {isToday ? (
                  <Badge variant="primary" size="sm">
                    {t("calendar.todayBadge")}
                  </Badge>
                ) : null}
              </div>

              <p
                className={cn(
                  "mt-auto flex items-start gap-1.5 text-xs leading-snug tabular-nums",
                  hasBookings ? "text-surface-700" : "text-surface-400",
                )}
              >
                {hasBookings ? (
                  <span
                    className="mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
                    aria-hidden
                  />
                ) : null}
                <span className="min-w-0">
                  {!hasBookings
                    ? t("calendar.dayStripEmpty")
                    : covers > 0
                      ? t("calendar.dayStripSummary", {
                          bookings: bookingCount,
                          covers,
                        })
                      : t("bookingCount", {
                          count: bookingCount,
                        })}
                </span>
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
